// Local editorial machine-draft translations only. Not an approval or release.
// If an English source changes, the pinned revision fails closed until every
// affected machine draft has been rechecked and the baseline is renewed.
import { sourceRevision } from '../lib/translationAssurance.js';
import { NEONATAL_PARENT_SOURCE_ROWS } from './neonatalParentTranslationSource.js';
import { neonatalQaCorrections } from './neonatalParentQaCorrections.js';
import ar from './neonatalParentDraftPacks/ar.js';
import bn from './neonatalParentDraftPacks/bn.js';
import es from './neonatalParentDraftPacks/es.js';
import fa from './neonatalParentDraftPacks/fa.js';
import fr from './neonatalParentDraftPacks/fr.js';
import gu from './neonatalParentDraftPacks/gu.js';
import it from './neonatalParentDraftPacks/it.js';
import pa from './neonatalParentDraftPacks/pa.js';
import pl from './neonatalParentDraftPacks/pl.js';
import pt from './neonatalParentDraftPacks/pt.js';
import ro from './neonatalParentDraftPacks/ro.js';
import so from './neonatalParentDraftPacks/so.js';
import tl from './neonatalParentDraftPacks/tl.js';
import tr from './neonatalParentDraftPacks/tr.js';
import ur from './neonatalParentDraftPacks/ur.js';
import zh from './neonatalParentDraftPacks/zh.js';

export const NEONATAL_DRAFT_LOCALES = Object.freeze(["ar","bn","es","fa","fr","gu","it","pa","pl","pt","ro","so","tl","tr","ur","zh"]);
// Reviewed editorial rebaseline for the single updated English safer-sleep source.
// Clinical and native-speaker approval are STILL REQUIRED; this only preserves
// source parity for the draft preview. Every later English change fails closed.
export const NEONATAL_DRAFT_SOURCE_REVISION = 'd0dc21d6';
export const NEONATAL_PARENT_MACHINE_DRAFTS = Object.freeze({ ar, bn, es, fa, fr, gu, it, pa, pl, pt, ro, so, tl, tr, ur, zh });
export const NEONATAL_DRAFT_PROVENANCE = 'machine_unreviewed';
export function neonatalSourceRevision() {
  const entries = NEONATAL_PARENT_SOURCE_ROWS.map(row => [row.id, row.sourceText]);
  return sourceRevision('neonatalParent:corpus', JSON.stringify(entries.sort((a, b) => a[0].localeCompare(b[0]))));
}
export function neonatalDraftMissingKeys(locale) {
  const code = String(locale || '').toLowerCase().split('-')[0];
  const pack = NEONATAL_PARENT_MACHINE_DRAFTS[code];
  if (!pack) return NEONATAL_PARENT_SOURCE_ROWS.map(row => row.path);
  return NEONATAL_PARENT_SOURCE_ROWS.filter(row =>
    typeof pack[row.path] !== 'string' || !pack[row.path].trim() || pack[row.path].trim() === 'Loading...' || /^#(VALUE|REF|N\/A|ERROR)/.test(pack[row.path].trim())).map(row => row.path);
}
// Source and bundled draft packs are static for the lifetime of this build.
// Calculate completeness once, not for every visible paragraph on an iPhone.
const SOURCE_CURRENT_AT_LOAD = neonatalSourceRevision() === NEONATAL_DRAFT_SOURCE_REVISION;
const LOCALE_READY_AT_LOAD = Object.freeze(Object.fromEntries(
  NEONATAL_DRAFT_LOCALES.map(code =>
    [code, SOURCE_CURRENT_AT_LOAD && neonatalDraftMissingKeys(code).length === 0]),
));
export function neonatalDraftLocaleReady(locale) {
  const code = String(locale || '').toLowerCase().split('-')[0];
  return code === 'en' || LOCALE_READY_AT_LOAD[code] === true;
}
export function neonatalDraftText(locale, path, english) {
  const code = String(locale || 'en').toLowerCase().split('-')[0];
  if (code === 'en' || !neonatalDraftLocaleReady(code)) return english;
  const corrected = neonatalQaCorrections(code)[path];
  const text = corrected || NEONATAL_PARENT_MACHINE_DRAFTS[code][path];
  // Dialable UK emergency number: prevent localisation into non-Latin digits.
  if (path === 'ui.safetyNetting' || path === 'modules.after-neonatal-care.topics.illness.paragraphs.1') {
    return text.replaceAll('۹۹۹', '999').replaceAll('৯৯৯', '999');
  }
  return text;
}
